
import { apiRequest, SUPABASE_URL } from "./apiUtils";

// Simple API wrapper for direct requests
export async function apiWrapper<T = any>(
  url: string,
  options?: RequestInit
): Promise<T> {
  // If URL doesn't start with http, prepend SUPABASE_URL
  const fullUrl = url.startsWith('http') ? url : `${SUPABASE_URL}${url}`;
  return apiRequest<T>(fullUrl, options);
}

// Generic API wrapper to generate consistent API functions
export function createApiWrapper<T>(resourceName: string) {
  return {
    list: async (options?: { queryKey?: any }) => {
      // Extract search parameters from queryKey if available
      let params = '';
      if (options?.queryKey && Array.isArray(options.queryKey) && options.queryKey.length > 1) {
        const searchParams = options.queryKey[1];
        if (searchParams) {
          const queryParams = new URLSearchParams();
          
          if (searchParams.search) {
            queryParams.append('search', searchParams.search);
          }
          
          if (searchParams.limit) {
            queryParams.append('limit', searchParams.limit.toString());
          }
          
          if (searchParams.offset !== undefined) {
            queryParams.append('offset', searchParams.offset.toString());
          }
          
          // Add any other parameters
          Object.entries(searchParams).forEach(([key, value]) => {
            if (key !== 'search' && key !== 'limit' && key !== 'offset' && value !== undefined) {
              queryParams.append(key, String(value));
            }
          });
          
          const queryString = queryParams.toString();
          if (queryString) {
            params = `?${queryString}`;
          }
        }
      }
      
      return apiRequest<T[]>(`${SUPABASE_URL}/${resourceName}${params}`);
    },

    getById: async (id: number) => {
      return apiRequest<T>(`${SUPABASE_URL}/${resourceName}/${id}`);
    },

    create: async (data: Partial<T>) => {
      return apiRequest<T>(`${SUPABASE_URL}/${resourceName}`, {
        method: 'POST',
        body: data
      });
    },

    update: async (id: number, data: Partial<T>) => {
      return apiRequest<T>(`${SUPABASE_URL}/${resourceName}/${id}`, {
        method: 'PATCH',
        body: data
      });
    },

    delete: async (id: number) => {
      return apiRequest<void>(`${SUPABASE_URL}/${resourceName}/${id}`, {
        method: 'DELETE'
      });
    },

    // Add any additional methods as needed
    attachFile: async (id: number, file: File, fieldName: string = 'file') => {
      const formData = new FormData();
      formData.append(fieldName, file);
      
      return apiRequest<T>(`${SUPABASE_URL}/${resourceName}/${id}/attachment`, {
        method: 'POST',
        body: formData
      });
    }
  };
}
